import { useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import { FeatureFlag } from '../../constants/featureFlags';
import VisitCompletionByClinician from './VisitCompletionByClinician';
import VisitCompletionByClinicianAndBranch from './VisitCompletionByClinicianAndBranch';

export interface AdoptionReportRef {
  exportToCSV: () => void;
}

const AdoptionReport = forwardRef<AdoptionReportRef>((_props, ref) => {
  const { hasFeatureFlag } = useStore(
    useShallow(state => ({
      hasFeatureFlag: state.hasFeatureFlag,
    }))
  );

  const hasBranchReportFlag = hasFeatureFlag(
    FeatureFlag.ADOPTION_REPORT_BY_BRANCH
  );

  const [reportType, setReportType] = useState<'clinician' | 'branch'>(
    'clinician'
  );
  const [exportHandler, setExportHandler] = useState<(() => void) | null>(null);

  // The page header owns the export button, while the active report table owns
  // the actual CSV logic. Expose one stable method so the header can call the
  // export handler registered by the currently selected report type.

  // Must be stable: children register their export handler from an effect that
  // depends on this callback. An unstable identity re-triggers that effect every
  // render, causing an infinite re-render loop once report data is present (which
  // in turn tore down the date-picker calendar on each frame).
  const handleExportRegistry = useCallback((handler: (() => void) | null) => {
    setExportHandler(() => handler);
  }, []);

  const exportToCSV = () => {
    if (exportHandler) {
      exportHandler();
    }
  };

  useImperativeHandle(ref, () => ({
    exportToCSV,
  }));

  return (
    <div className='space-y-4'>
      <div className='flex justify-between items-center'>
        {hasBranchReportFlag ? (
          <div className='w-[400px]'>
            <Select
              value={reportType}
              onValueChange={value =>
                setReportType(value as 'clinician' | 'branch')
              }
            >
              <SelectTrigger>
                <SelectValue placeholder='Select report type' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='clinician'>
                  Adoption Report - Visit Completion by Clinician
                </SelectItem>
                <SelectItem value='branch'>
                  Adoption Report - Visit Completion By Clinician And Branch
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        ) : (
          <h2 className='text-lg font-semibold text-foreground'>
            Adoption Report - Visit Completion by Clinician
          </h2>
        )}
      </div>

      {reportType === 'branch' && hasBranchReportFlag ? (
        <VisitCompletionByClinicianAndBranch
          onExportRegistry={handleExportRegistry}
        />
      ) : (
        <VisitCompletionByClinician onExportRegistry={handleExportRegistry} />
      )}
    </div>
  );
});

AdoptionReport.displayName = 'AdoptionReport';

export default AdoptionReport;
